import { useSelector } from 'react-redux';
import { Empty } from 'antd';
import { PractitionerSettings } from 'src/models/practitioner';
import MemoryPreferencesForm from './MemoryPreferencesForm';
import { useMemoryPreferences } from './useMemoryPreferences';

type PractitionerWithRoles = {
  roles?: string[];
};

type RootState = {
  practitionerState: {
    currentPractitioner?: PractitionerWithRoles;
    currentPractitionerSettings?: PractitionerSettings;
  };
};

const TeamPreferences = () => {
  const { currentPractitioner, currentPractitionerSettings } = useSelector(
    (state: RootState) => state.practitionerState
  );
  const isMemoryEnabled = currentPractitionerSettings?.note_preferences?.memory;
  const canManageTeamMemory = currentPractitioner?.roles?.some((role) =>
    ['admin', 'superadmin'].includes(role)
  );
  const {
    currentKeywordInput,
    handleAddKeyword,
    handleRemoveKeyword,
    instructions,
    keywordError,
    keywords,
    setCurrentKeywordInput,
    setInstructions,
  } = useMemoryPreferences({
    endpoint: '/v2/memory/team',
    canLoad: canManageTeamMemory,
    canSave: canManageTeamMemory && isMemoryEnabled,
    fetchErrorLogMessage: 'Failed to fetch team memory preferences',
    saveErrorLogMessage: 'Failed to update team memory preferences',
    saveErrorToastMessage: 'Failed to save team memory preferences.',
  });

  if (!canManageTeamMemory) {
    return (
      <div className="py-8">
        <Empty description="Only admins can manage team memory." />
      </div>
    );
  }

  return (
    <MemoryPreferencesForm
      title="Team Preferences"
      titlePopoverContent="Provide organization-level instructions and keywords that should improve note generation and transcription accuracy for the full team."
      knowledgeBaseLabel="Team Knowledge Base"
      knowledgeBaseDescription="Add organization-specific guidance like preferred provider spellings, practice conventions, or terminology to apply across the team."
      keywordLabel="Team keyword detection"
      keywordDescription="Add up to 20 team-level keywords or phrases."
      keywordPopoverContent="Add organization-specific provider names, medication names, and other phrases that should be recognized accurately for everyone."
      keywordPlaceholder="E.g: Dr. Smith"
      emptyKeywordsText="No team keywords added yet."
      instructions={instructions}
      setInstructions={setInstructions}
      keywords={keywords}
      currentKeywordInput={currentKeywordInput}
      setCurrentKeywordInput={setCurrentKeywordInput}
      keywordError={keywordError}
      handleAddKeyword={handleAddKeyword}
      handleRemoveKeyword={handleRemoveKeyword}
      disabled={!isMemoryEnabled}
    />
  );
};

export default TeamPreferences;
